import { _mapValues, _merge } from '@epihub/core/src/fn/object';
import { _uniq } from '@epihub/core/src/fn/array';
import { Color } from '@epihub/web/src/color';
import { Dimension, Screen } from '@epihub/web/src/dimensions';
import { type ThemeDefV0 } from './ThemeDefV0';

export function enhanceThemeDefV0WithMantine(opts: {
  theme: ThemeDefV0['Theme'];
  creator: ThemeDefV0['Creator'];
  state: ThemeDefV0['State'];
}) {
  opts.theme.mantine = opts.theme.mantine ?? opts.creator.mantine;

  opts.theme.col = {
    epilogo: new Color('#5240A6'),
    epilogoStrong: new Color('#A600FA'),
    epilogoAccent: new Color('#D15413'),
    light: new Color(opts.creator.mantine!.colors?.blue?.[0]!),
    dark: new Color(opts.creator.mantine!.colors?.dark?.[6]!),

    theme: (() => {
      const primary = opts.creator.mantine!.primaryColor
        ?? 'blue';

      const primaryShadeLight = (opts.creator.mantine!.primaryShade as any)?.light
        ?? 6;
      const primaryShadeDark = (opts.creator.mantine!.primaryShade as any)?.dark
        ?? 5;

      return new Color(
        opts.creator.mantine!.colors?.[primary]?.[
          opts.state.scheme === 'light'
            ? primaryShadeLight
            : primaryShadeDark
        ]!,
      );
    })(),

    accent: new Color(opts.creator.mantine!.colors?.orange?.[6]!),
    primary: opts.state.scheme === 'light'
      ? new Color(opts.creator.mantine!.black!)
      : new Color(opts.creator.mantine!.colors?.gray?.[2]!),
    secondary: opts.state.scheme === 'light'
      ? new Color(opts.creator.mantine!.colors?.gray?.[7]!)
      : new Color(opts.creator.mantine!.colors?.dark?.[1]!),
    tertiary: opts.state.scheme === 'light'
      ? new Color(opts.creator.mantine!.colors?.gray?.[5]!)
      : new Color(opts.creator.mantine!.colors?.dark?.[3]!),
    error: new Color(opts.creator.mantine!.colors?.red?.[6]!),
    success: new Color(opts.creator.mantine!.colors?.green?.[6]!),
    warning: new Color(opts.creator.mantine!.colors?.yellow?.[6]!),
    info: new Color(opts.creator.mantine!.colors?.blue?.[6]!),

    danger: new Color(opts.creator.mantine!.colors?.red?.[6]!),

    text: opts.state.scheme === 'light'
      ? new Color(opts.creator.mantine!.black!)
      : new Color(opts.creator.mantine!.colors?.dark?.[0]!),
    textSecondary: opts.state.scheme === 'light'
      ? new Color(opts.creator.mantine!.colors?.gray?.[7]!)
      : new Color(opts.creator.mantine!.colors?.dark?.[1]!),
    textTertiary: opts.state.scheme === 'light'
      ? new Color(opts.creator.mantine!.colors?.gray?.[6]!)
      : new Color(opts.creator.mantine!.colors?.dark?.[2]!),
    textDimmed: opts.state.scheme === 'light'
      ? new Color(opts.creator.mantine!.colors?.gray?.[5]!)
      : new Color(opts.creator.mantine!.colors?.dark?.[3]!),
    textPlaceholder: opts.state.scheme === 'light'
      ? new Color(opts.creator.mantine!.colors?.gray?.[4]!)
      : new Color(opts.creator.mantine!.colors?.dark?.[4]!),
    link: new Color(opts.creator.mantine!.colors?.blue?.[6]!),

    background: opts.state.scheme === 'light'
      ? new Color(opts.creator.mantine!.white!)
      : new Color(opts.creator.mantine!.colors?.dark?.[7]!),

    backgroundSecondary: opts.state.scheme === 'light'
      ? new Color(opts.creator.mantine!.colors?.gray?.[0]!)
      : new Color(opts.creator.mantine!.colors?.dark?.[6]!),

    backgroundTertiary: opts.state.scheme === 'light'
      ? new Color(opts.creator.mantine!.colors?.gray?.[1]!)
      : new Color(opts.creator.mantine!.colors?.dark?.[5]!),

    border: opts.state.scheme === 'light'
      ? new Color(opts.creator.mantine!.colors?.gray?.[3]!)
      : new Color(opts.creator.mantine!.colors?.dark?.[4]!),

    disabled: opts.state.scheme === 'light'
      ? new Color(opts.creator.mantine!.colors?.gray?.[4]!)
      : new Color(opts.creator.mantine!.colors?.dark?.[5]!),

    ..._mapValues(opts.creator.colors, (c) => new Color(c)),
    ...opts.theme.col as Partial<typeof opts.theme.col>,
  };

  opts.theme.font = _merge({}, opts.theme.font, {
    family: {
      base: [
        ...opts.creator.mantine!.fontFamily ? [opts.creator.mantine!.fontFamily] : [],
        '-apple-system',
        'BlinkMacSystemFont',
        'Segoe UI',
        'Roboto',
        'Helvetica',
        'Arial',
        'sans-serif',
        'Apple Color Emoji',
        'Segoe UI Emoji',
      ],
      mono: [
        ...opts.creator.mantine!.fontFamilyMonospace ? [opts.creator.mantine!.fontFamilyMonospace] : [],
        'SFMono-Regular',
        'Menlo',
        'Monaco',
        'Consolas',
        'Liberation Mono',
        'Courier New',
        'monospace',
      ],
    },
  });

  opts.theme.font.family!.base = _uniq(opts.theme.font.family!.base);
  opts.theme.font.family!.monospace = _uniq(opts.theme.font.family!.monospace);

  opts.theme.font.header = { size: new Dimension('24px') };
  opts.theme.font.base = { size: new Dimension('25px') };

  opts.theme.screen = new Screen(
    typeof window !== 'undefined' && window.innerWidth ? window.innerWidth : 375,
    typeof window !== 'undefined' && window.innerHeight ? window.innerHeight : 812,
  );
}
